/**
 * The frame every screen sits in.
 *
 * Responsibilities, in the order they appear on screen:
 *
 *   1. The preview banner, when Admin is looking at the app as a student. It
 *      is the first thing in the DOM and it is impossible to miss on purpose —
 *      mistaking a preview for the real thing is how someone concludes their
 *      content did not publish.
 *   2. A header that changes shape with the route: a title and identity at the
 *      top level, a back affordance inside a semester.
 *   3. The update prompt, when a new version has been downloaded.
 *
 * Screens own their own content and nothing else. They never draw a header,
 * never think about the notch, never mount navigation.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bell, EyeOff, LayoutGrid, RefreshCw, Search, Settings as SettingsIcon } from 'lucide-react';

import { useSession } from '../../state/SessionProvider';
import { applyUpdate, onUpdateReady } from '../../lib/serviceWorker';
import { refreshPushRegistration } from '../../lib/push';
import { useUnreadCount } from '../../data/queries';
import { Avatar } from '../ui/Avatar';
import { InstallCoach } from './InstallCoach';
import { PushInvite } from './PushInvite';

export function AppShell({ children }: { children: ReactNode }) {
  const { viewAsStudent, setViewAsStudent, can } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [updateReady, setUpdateReady] = useState(false);

  useEffect(() => onUpdateReady(() => setUpdateReady(true)), []);

  /*
   * Messages from the service worker.
   *
   * NAVIGATE: a notification was tapped in a browser that cannot navigate an
   * existing client directly (Safari). Routing it here keeps the deep link
   * working without a full page load.
   *
   * RESUBSCRIBE_PUSH: the push service rotated this device's subscription. The
   * worker cannot re-register it — that needs the signed-in session — so it
   * asks the page to.
   */
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; url?: string } | undefined;
      if (data?.type === 'NAVIGATE' && data.url) navigate(data.url);
      if (data?.type === 'RESUBSCRIBE_PUSH') void refreshPushRegistration();
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  /*
   * Scroll to the top on navigation.
   *
   * Only when the path itself changes: a query-string change is a filter or a
   * tab, and yanking the reader back to the top for one of those loses their
   * place for no reason.
   */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [location.pathname]);

  const insideSemester = location.pathname.startsWith('/s/');
  const insideAdmin = location.pathname.startsWith('/admin');

  return (
    <div className="shell">
      {viewAsStudent ? (
        <div className="preview-bar" role="status">
          <EyeOff size={15} aria-hidden="true" />
          <span className="preview-bar__label">
            Viewing as a student<span className="preview-bar__detail"> · drafts and unpublished content are hidden</span>
          </span>
          <button type="button" className="preview-bar__exit" onClick={() => setViewAsStudent(false)}>
            Exit
          </button>
        </div>
      ) : null}

      {/* The console draws its own header; everywhere else shares this one. */}
      {!insideAdmin ? <TopBar compact={insideSemester} showConsoleLink={can('admin:console')} /> : null}

      {updateReady ? (
        <div className="container">
          <div className="update-note" role="status">
            <RefreshCw size={15} aria-hidden="true" />
            <span>A new version of SamuelSchool is ready.</span>
            <button type="button" className="btn btn--sm btn--tinted" onClick={applyUpdate}>
              Reload
            </button>
          </div>
        </div>
      ) : null}

      {/*
        The notification invite sits above the routed content rather than
        inside a screen: it has to be reachable from wherever somebody lands
        after installing, and Home is not always that place.
      */}
      <div className="container">
        <PushInvite />
      </div>

      {children}

      <InstallCoach />
    </div>
  );
}

/**
 * The top bar.
 *
 * Inside a semester it goes quiet — the semester's own header takes over the
 * identity of the screen, and two competing titles is one too many. What
 * survives everywhere is the route back to the semester list, the bulletin,
 * and the account.
 */
function TopBar({ compact, showConsoleLink }: { compact: boolean; showConsoleLink: boolean }) {
  const { photoURL, displayName } = useSession();
  const location = useLocation();
  const searchPath = !showConsoleLink && location.pathname.startsWith('/s/') ? `/search?semester=${encodeURIComponent(location.pathname.split('/')[2] ?? '')}` : '/search';
  const unread = useUnreadCount();
  const navigate = useNavigate();

  return (
    <header className={`topbar${compact ? ' topbar--compact' : ''}`}>
      <div className="container topbar__inner">
        <Link to="/" className="topbar__brand" aria-label="SamuelSchool home">
          <span className="topbar__mark" aria-hidden="true">
            S
          </span>
          {!compact ? <span className="topbar__word">SamuelSchool</span> : null}
        </Link>

        <div className="spacer" />

        <Link to={searchPath} className="icon-btn" aria-label="Search the site" title="Search the site"><Search size={20} /></Link>

        {showConsoleLink ? (
          <Link to="/admin" className="icon-btn" aria-label="Admin console" title="Admin console">
            <LayoutGrid size={20} />
          </Link>
        ) : null}

        <Link to="/bulletin" className="icon-btn topbar__bell" aria-label={badgeLabel(unread)}>
          <Bell size={20} />
          {unread > 0 ? (
            <span className="count topbar__count" aria-hidden="true">
              {unread > 99 ? '99+' : unread}
            </span>
          ) : null}
        </Link>

        <button
          type="button"
          className="topbar__account"
          onClick={() => navigate('/settings')}
          aria-label="Settings and account"
        >
          <Avatar name={displayName} src={photoURL} size={32} />
        </button>

        <Link to="/settings" className="icon-btn topbar__settings" aria-label="Settings">
          <SettingsIcon size={20} />
        </Link>
      </div>
    </header>
  );
}

function badgeLabel(unread: number): string {
  if (unread === 0) return 'Bulletin';
  return `Bulletin, ${unread} unread`;
}
